import React, { useCallback } from 'react';
import { useT } from '@transifex/react';
import { useIntl } from 'react-intl';
import CurrencyInput from 'react-currency-input-field';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import messages from 'messages';
import { ChartHeaderProps } from '../types';
import { useForm } from './form';
import * as S from './styles';
import { useFormats } from '@raisin/investments-data/util/useFormats';
import {
  FIRST_INVESTMENT_MIN,
  FIRST_INVESTMENT_MIN_NEW,
  MONTHLY_PLAN_MIN,
  MONTHLY_PLAN_MIN_NEW,
} from '../types';

const ChartHeader: React.FC<ChartHeaderProps> = ({
  startAmount,
  monthlyAmount,
  portfolio,
  duration,
  changeStartAmount,
  changeMonthlyAmount,
  changePortfolio,
  changeDuration,
  hasMoneyMarket,
}) => {
  const t = useT();
  const { locale } = useIntl();
  const { formatMoneyAmount } = useFormats();
  const minFirstInvestment = hasMoneyMarket ? FIRST_INVESTMENT_MIN_NEW : FIRST_INVESTMENT_MIN;
  const minMonthlyPlan = hasMoneyMarket ? MONTHLY_PLAN_MIN_NEW : MONTHLY_PLAN_MIN;
  const formik = useForm(
    {
      firstInvestment: startAmount,
      monthlyPlan: monthlyAmount,
    },
    minFirstInvestment,
    minMonthlyPlan,
  );

  const onStartAmountChange = useCallback(
    (_, __, values) => {
      changeStartAmount(values?.float);
    },
    [changeStartAmount, formik.errors.firstInvestment],
  );

  const onMonthlyAmountChange = useCallback(
    (_, __, values) => {
      changeMonthlyAmount(values?.float);
    },
    [changeMonthlyAmount, formik.errors.monthlyPlan],
  );

  return (
    <>
      <Grid item xs={12} mb={8}>
        <S.Title variant="h3" mb={6} textAlign="left">
          {t(messages.DWMPrognoseChartHeading)}
        </S.Title>
        <S.SubTitle variant="subtitle1">{t(messages.DWMPrognoseChartSubHeading)}</S.SubTitle>
        <S.Description variant="body1" mt={2}>
          {t(messages.DWMPrognoseChartParagraph)}
        </S.Description>
      </Grid>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={4}
      >
        <Box width="100%">
          <FormControl fullWidth variant="standard">
            <S.TextField
              label={
                <S.Label variant="body2" $hasError={formik.errors.firstInvestment}>
                  {t(messages.DWMPrognoseChartInputsOneTimeInvestmentLabel)}
                </S.Label>
              }
              placeholder={t(messages.DWMPrognoseChartInputsOneTimeInvestmentPlaceholder)}
              $noPadding
              name="startAmount"
              form={formik}
              field="firstInvestment"
              value={startAmount}
              helperText={
                formik.errors.firstInvestment &&
                t(
                  messages[
                    `DWMPrognoseChartInputsOneTimeInvestmentHelper${formik.errors.firstInvestment}`
                  ],
                  {
                    amount: formatMoneyAmount({ value: minFirstInvestment, currency: 'eur' }),
                  },
                )
              }
              error={formik.errors.firstInvestment}
              InputProps={{
                startAdornment: <S.Adornment position="start">&euro;</S.Adornment>,
                inputComponent: CurrencyInput,
                inputProps: {
                  onValueChange: onStartAmountChange,
                  intlConfig: { locale: locale },
                },
              }}
            />
          </FormControl>
        </Box>
        <Box width="100%">
          <FormControl fullWidth variant="standard">
            <S.TextField
              label={
                <S.Label variant="body2" $hasError={formik.errors.monthlyPlan}>
                  {t(messages.DWMPrognoseChartInputsMonthlySavingRateLabel)}
                </S.Label>
              }
              placeholder={t(messages.DWMPrognoseChartInputsMonthlySavingRatePlaceholder)}
              $noPadding
              name="saveingRate"
              form={formik}
              field="monthlyPlan"
              value={monthlyAmount}
              helperText={
                formik.errors.monthlyPlan &&
                t(
                  messages[
                    `DWMPrognoseChartInputsMonthlySavingRateHelper${formik.errors.monthlyPlan}`
                  ],
                  {
                    amount: formatMoneyAmount({
                      value: minMonthlyPlan,
                      currency: 'eur',
                    }),
                  },
                )
              }
              error={formik.errors.monthlyPlan}
              InputProps={{
                startAdornment: <S.Adornment position="start">&euro;</S.Adornment>,
                inputComponent: CurrencyInput,
                inputProps: {
                  intlConfig: { locale: locale },
                  onValueChange: onMonthlyAmountChange,
                },
              }}
            />
          </FormControl>
        </Box>
        <Box width="100%">
          <S.TextField
            fullWidth
            id="portfolio"
            select
            label={
              <S.Label variant="body2">{t(messages.DWMPrognoseChartInputsDurationLabel)}</S.Label>
            }
            defaultValue={duration}
            onChange={(e) => changeDuration(parseInt(e.target.value.toString()))}
            SelectProps={{
              native: true,
            }}
          >
            {[5, 10, 20].map((duration) => (
              <option value={duration} key={duration}>
                {`${duration} ${t(messages.DWMPrognoseChartBoxDuration)}`}
              </option>
            ))}
          </S.TextField>
        </Box>
        <Box width="100%">
          <S.TextField
            fullWidth
            id="portfolio"
            select
            label={
              <S.Label variant="body2">{t(messages.DWMPrognoseChartInputsPortfolioLabel)}</S.Label>
            }
            defaultValue={portfolio}
            onChange={(e) => changePortfolio(parseInt(e.target.value.toString()))}
            SelectProps={{
              native: true,
            }}
          >
            {hasMoneyMarket && (
              <option value={4}>{t(messages.DWMPrognoseChartInputsPortfolioValueMM)}</option>
            )}
            <option value={0}>{t(messages.DWMPrognoseChartInputsPortfolioValue30)}</option>
            <option value={1}>{t(messages.DWMPrognoseChartInputsPortfolioValue50)}</option>
            <option value={2}>{t(messages.DWMPrognoseChartInputsPortfolioValue70)}</option>
            <option value={3}>{t(messages.DWMPrognoseChartInputsPortfolioValue100)}</option>
          </S.TextField>
        </Box>
      </Box>
    </>
  );
};

export default ChartHeader;
